import React, {useEffect} from 'react'
import {
  Chip,
  Grid,
  Stack,
  MenuItem,
  Skeleton,
  Button,
  Alert,
} from '@mui/material'
import surveysApi from '../../services/api/surveys/surveysApi'
import WbButton from '../../components/common/WbButton'
import {useNavigate} from 'react-router-dom'
import CreateSurvey from '../SurveyPanel/CreateSurvey'
import AddIcon from '@mui/icons-material/Add'
import WbTextField from '../../components/common/WbTextField'
import SurveyCard from './SurveyCard/SurveyCard'
import welbeelogo from '../../components/assets/images/welbee_logo.svg'
import Header from '../../components/layouts/header/Header'
import useCustomerData from '../../hooks/useCustomerData'
import DashboardTabs from './DashboardTabs'
import Menu from './Menu'
import useUserData from '../../hooks/useUserData'
import trackUserInteraction from '../../utils/trackUserInteraction'
import {
  canCreateSurvey,
  canChangeLiveSurveyEndDate,
  getMatSites,
} from '../../utils/utilities'
import {CUSTOMER_TYPE} from '../../utils/constants'
import {useIdentityAuth, getApiHookParams} from '../../auth'
import customerApi from '../../services/api/customer/customerApi'
import EnterKioskMode from '../../components/kiosk/EnterKioskMode'

export default function Dashboard() {
  const [surveys, setSurveys] = React.useState([])
  const [mysurvey, setMysurvey] = React.useState([])
  const [otherSurvey, setOtherSurvey] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [createNew, setCreateNew] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [status, setStatus] = React.useState('')
  const [sort, setSort] = React.useState(null)
  const [filteredSurveys, setFilteredSurveys] = React.useState([])
  const [view, setView] = React.useState(0)
  const [showTabs, setShowTabs] = React.useState(false)
  const [loadingOtherSurveys, setLoadingOtherSurveys] = React.useState(false)
  const [schoolList, setSchoolList] = React.useState([])
  const [selectedSchool, setSelectedSchool] = React.useState(-1)
  const [im, setIm] = React.useState(0)
  const navigate = useNavigate()

  const {user, authenticated, authLoading} = useIdentityAuth()
  const apiParams = getApiHookParams()
  //const customer = useCustomerData(

  useEffect(() => {
    loadSurveys()
    loadCustomer()
  }, [])

  // Lets another Welbee app (EI) link straight into survey creation with
  // ?createSurvey=1, rather than dropping the user on the dashboard to find
  // the button again. Gated on the same permission as the button itself, and
  // the flag is stripped afterwards so a refresh doesn't reopen the dialog.
  useEffect(() => {
    if (!user || !canCreateSurvey(user)) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('createSurvey') !== '1') return

    setCreateNew(true)
    params.delete('createSurvey')
    const query = params.toString()
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (query ? `?${query}` : '')
    )
  }, [user])

  const loadCustomer = () => {
    customerApi
      .customerInfo({
        entityType:
          apiParams.groupId > 0 && apiParams.schoolId === null ? 1 : 0,
        entityIds: [
          apiParams.groupId > 0 && apiParams.schoolId === null
            ? apiParams.groupId
            : apiParams.schoolId,
        ],
        userId: '',
        email: '',
      })
      .then(response => {
        
        localStorage.setItem(
          'customer',
          JSON.stringify({
            name: response.data[0].SchoolName || response.data[0].GroupName,
          })
        )
      })
  }

  useEffect(() => {
    if (view === 0) {
      filter(mysurvey)
      setSurveys([...mysurvey])
      setIm(
        apiParams.groupId > 0 && apiParams.schoolId === null
          ? CUSTOMER_TYPE.MAT
          : CUSTOMER_TYPE.School
      )

      // Track my surveys view
      if (mysurvey.length > 0) {
        trackUserInteraction('Dashboard', 'ViewMySurveys', 'My Surveys Tab', {
          surveyCount: mysurvey.length,
        })
      }
    } else {
      filter(otherSurvey)
      setSurveys([...otherSurvey])
      setIm(apiParams.groupId > 0 && apiParams.schoolId === null ? 2 : 1)

      // Track other surveys view
      if (otherSurvey.length > 0) {
        trackUserInteraction(
          'Dashboard',
          'ViewOtherSurveys',
          'Other Surveys Tab',
          {
            surveyCount: otherSurvey.length,
            viewType:
              apiParams.groupId > 0 && apiParams.schoolid
                ? 'SchoolSurveys'
                : 'MatSurveys',
          }
        )
      }
    }
  }, [view])

  const updateListOnDelete = id => {
    const updatedSurveys = surveys.filter(s => s.id !== id)
    setSurveys(updatedSurveys)
    if (view === 0) {
      const updatedMySurveys = mysurvey.filter(s => s.id !== id)
      setMysurvey(updatedMySurveys)
      filter(updatedMySurveys)
    } else {
      const updatedOtherSurveys = otherSurvey.filter(s => s.id !== id)
      setOtherSurvey(updatedOtherSurveys)
      filter(updatedOtherSurveys)
    }
  }

  const loadSurveys = () => {
    setLoading(true)
    surveysApi
      .getAll(
        apiParams.groupId > 0 && apiParams.schoolId === null
          ? apiParams.groupId
          : apiParams.schoolId,
        apiParams.groupId > 0 && apiParams.schoolId === null
          ? CUSTOMER_TYPE.MAT
          : CUSTOMER_TYPE.School,
        50,
        apiParams.groupId > 0 && apiParams.schoolId === null
          ? getMatSites(apiParams)
          : null
      )
      .then(res => {
        let fs = res.sort((a, b) => a.name.localeCompare(b.name))
        setSurveys([...fs])
        setFilteredSurveys([...fs])
        setMysurvey([...fs])

        // Track successful surveys load
        trackUserInteraction('Dashboard', 'LoadSurveys', 'Surveys Loaded', {
          surveyCount: fs.length,
        })
      })
      .catch(err => {
        // Track error in survey loading
        trackUserInteraction('Dashboard', 'Error', 'Failed to Load Surveys', {
          errorDetails: err?.message || 'Unknown error',
        })
      })
      .finally(() => setLoading(false))

    if (apiParams.groupId > 0 && apiParams.schoolId === null) {
      setLoadingOtherSurveys(true)
      surveysApi
        .getSchoolsofMatSurveys(apiParams.groupId, getMatSites(apiParams))
        .then(res => {
          loadOtherSurveys(res)
          loadSchoolList(res)

          // Track MAT schools surveys loaded
          trackUserInteraction(
            'Dashboard',
            'LoadMatSchoolSurveys',
            'MAT School Surveys',
            {
              schoolCount: new Set(res.map(s => s.schoolId)).size,
              surveyCount: res.length,
            }
          )
        })
        .catch(err => {})
        .finally(() => setLoadingOtherSurveys(false))
      setShowTabs(true)
    } else if (
      apiParams.groupId === null &&
      apiParams.schoolId > 0 &&
      apiParams.group
    ) {
      // is mat school
      setLoadingOtherSurveys(true)
      surveysApi
        .getMatSurveysForSchool(apiParams.group.group_id, apiParams.schoolId)
        .then(res => {
          loadOtherSurveys(res)

          // Track MAT surveys for school loaded
          trackUserInteraction(
            'Dashboard',
            'LoadMatSurveys',
            'MAT Surveys for School',
            {
              surveyCount: res.length,
              schoolId: apiParams.schoolId,
              matId: apiParams.groupId,
            }
          )
        })
        .catch(err => {})
        .finally(() => setLoadingOtherSurveys(false))
      setShowTabs(true)
    } else {
      setShowTabs(false)
      setView(0)
    }
  }

  useEffect(() => {
    filter(surveys)

    // Track filter changes if user has applied filters
    if (search || status || sort !== null || selectedSchool > -1) {
      trackUserInteraction('Dashboard', 'Filter', 'Applied Filters', {
        hasSearch: search !== '',
        hasStatusFilter: status !== '',
        sortType: getSortTypeLabel(sort),
        hasSchoolFilter: selectedSchool > -1,
        resultCount: filteredSurveys.length,
      })
    }
  }, [search, status, sort, selectedSchool])

  // Helper function to get sort type label for analytics
  const getSortTypeLabel = sortValue => {
    switch (sortValue) {
      case 1:
        return 'A-Z'
      case 2:
        return 'Newest'
      case 3:
        return 'Oldest'
      case 4:
        return 'Z-A'
      default:
        return 'None'
    }
  }

  const loadOtherSurveys = res => {
    let fs = res.sort((a, b) => a.name.localeCompare(b.name))
    setOtherSurvey([...fs])
  }

  const loadSchoolList = res => {
    let schools = []
    res.map(s => {
      if (schools.findIndex(c => c.id === s.schoolId) === -1)
        schools.push({id: s.schoolId, name: s.schoolName})
    })
    // Filter schools to only include those from apiParams.schoolIds
    // When hasAllSchoolsAccess = true, apiParams.schoolIds contains all accessible schools
    // When hasAllSchoolsAccess = false, apiParams.schoolIds contains only limited schools
    if (apiParams.schoolIds && apiParams.schoolIds.length > 0) {
      schools = schools.filter(school => 
        apiParams.schoolIds.includes(school.id)
      )
    }
    setSchoolList([...schools])
  }

  const filter = surveysList => {
    var list = [
      ...surveysList?.filter(x => {
        return (
          (search === ''
            ? (x.name = x.name)
            : x.name.toLowerCase().indexOf(search.toLowerCase()) !== -1) &&
          filterStatus(x)
        )
      }),
    ]
    if (selectedSchool > -1 && view > 0) {
      list = list.filter(x => x.schoolId === selectedSchool)
    }
    if (sort === 1) {
      list = list.sort((a, b) => a.name.localeCompare(b.name))
    } else if (sort === 2) {
      list = list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    } else if (sort === 3) {
      list = list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    } else if (sort === 4) {
      list = list.sort((a, b) => b.name.localeCompare(a.name))
    }
    setFilteredSurveys(list)
  }

  const filterStatus = s => {
    if (status === '') return true
    return s.status === status
  }

  const handleCreateSurveyClick = () => {
    // Track user creating a new survey
    trackUserInteraction('Survey', 'CreateStart', 'Create Survey Button', {
      fromPage: 'Dashboard',
    })
    setCreateNew(true)
  }

  // Rest of component remains unchanged
  return (
    <>
      <Header />
      <div className="dashboard-container">
        <div className="left-container">
          <Stack direction="column" justifyContent="space-between" sx={{height: 'calc(100vh - 80px)'}}>
            <div>
              <div className="school-logo">
                <img src={welbeelogo} alt="School Logo" />
              </div>
              <div className="all-surveys">
                <Menu
                  status={status}
                  surveys={surveys}
                  setStatus={newStatus => {
                    // Track status filter changes
                    if (newStatus !== status) {
                      trackUserInteraction(
                        'Dashboard',
                        'FilterByStatus',
                        newStatus || 'All',
                        {
                          previousStatus: status || 'All',
                        }
                      )
                    }
                    setStatus(newStatus)
                  }}
                ></Menu>
              </div>
            </div>
            <div>
              <EnterKioskMode />
            </div>
          </Stack>
        </div>
        <div className="right-container">
          <div className="survey-dashboard">
            <Grid container>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <h1>All Surveys</h1>
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12} my={3}>
                <Grid container justifyContent="space-between" rowGap={1}>
                  <Grid item xl={6} lg={6} md={9} sm={12} xs={12}>
                    <Stack direction={'row'} spacing={1}>
                      {canCreateSurvey(user) && (
                        <WbButton
                          CustomButtonText={'Create new survey'}
                          onClick={handleCreateSurveyClick}
                          startIcon={<AddIcon />}
                        ></WbButton>
                      )}
                      <WbTextField
                        label="Sort"
                        onChange={e => setSort(e.target.value)}
                        select
                        sx={{width: '300px'}}
                      >
                        <MenuItem value={1} selected>
                          A-Z
                        </MenuItem>
                        <MenuItem value={4}>Z-A</MenuItem>
                        <MenuItem value={2}>Newest</MenuItem>
                        <MenuItem value={3}>Oldest</MenuItem>
                      </WbTextField>
                    </Stack>
                  </Grid>
                  <Grid item xl={3} lg={3} md={3} sm={12} xs={12}>
                    <WbTextField
                      label="Search"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    ></WbTextField>
                  </Grid>
                </Grid>
              </Grid>
              {createNew && (
                <CreateSurvey
                  onClose={() => {
                    trackUserInteraction(
                      'Survey',
                      'CreateCancel',
                      'Closed Create Survey Modal'
                    )
                    setCreateNew(false)
                  }}
                ></CreateSurvey>
              )}
            </Grid>
            {showTabs && (
              <DashboardTabs
                view={view}
                setView={setView}
                schoolList={schoolList}
                selectedSchool={selectedSchool}
                setSelectedSchool={setSelectedSchool}
              ></DashboardTabs>
            )}
            <Grid container spacing={2}>
              {(loading && view === 0) ||
              (loadingOtherSurveys && view !== 0) ? (
                <Grid item lg={12} md={12} sm={12} xs={12} my={5}>
                  <Skeleton animation="wave" />
                  <Skeleton animation="wave" />
                  <Skeleton animation="wave" />
                </Grid>
              ) : filteredSurveys.length === 0 &&
                ((!loading && view === 0) ||
                  (!loadingOtherSurveys && view !== 0)) ? (
                <Grid item xxl={12} xl={12} lg={12} md={12} sm={12} xs={12}>
                  <Alert severity="info">Nothing to display</Alert>
                </Grid>
              ) : (
                filteredSurveys?.map(survey => {
                  return (
                    <Grid
                      item
                      xxl={3}
                      xl={4}
                      lg={4}
                      md={6}
                      sm={6}
                      xs={12}
                      key={survey.id}
                    >
                      <SurveyCard
                        survey={survey}
                        loadSurveys={loadSurveys}
                        updateListOnDelete={updateListOnDelete}
                        canDoActions={view === 0}
                        canChangeEndDate={canChangeLiveSurveyEndDate(user)}
                        cType={im}
                      ></SurveyCard>
                    </Grid>
                  )
                })
              )}
            </Grid>
          </div>
        </div>
      </div>
      
    </>
  )
}
